import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MagnifyingGlassIcon, UserIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Patient } from '../types/patient';
import { patientService } from '../services/dataSource';
import { formatDobWithAge } from '../utils/dateFormat';
import { patientIdLabel, patientName } from '../utils/patientDisplay';
import { useOrganizationStore } from '../stores/organizationStore';

interface PatientSearchProps {
  onSelectPatient: (patient: Patient) => void;
  selectedPatientId?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
}

export const PatientSearch: React.FC<PatientSearchProps> = ({
  onSelectPatient,
  selectedPatientId,
  placeholder,
  label,
  required = false,
  disabled = false,
  error,
  className = ''
}) => {
  const { operationMode } = useOrganizationStore();
  // Standalone lists the MRN and cannot search the encrypted email and phone, so it says so
  const searchPlaceholder = placeholder
    ?? (operationMode === 'standalone' ? 'Search by name or MRN...' : 'Search by name, email, phone, or ID...');
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  const loadSelectedPatient = useCallback(async (patientId: string) => {
    try {
      const patient = await patientService.getPatient(patientId);
      setSelectedPatient(patient);
    } catch (error) {
      console.error('Failed to load selected patient:', error);
    }
  }, []);

  // Load selected patient if ID is provided
  useEffect(() => {
    if (selectedPatientId && !selectedPatient) {
      loadSelectedPatient(selectedPatientId);
    }
  }, [selectedPatientId, selectedPatient, loadSelectedPatient]);

  const searchPatients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await patientService.getPatients({
        search: searchTerm,
        limit: 10,
        isActive: true // Only show active patients
      });
      // Archived patients cannot be booked; drop any a data source returned despite the filter.
      setPatients(response.data.patients.filter(p => p.isActive !== false));
      setShowDropdown(true);
      setHighlightedIndex(-1);
    } catch (error) {
      console.error('Failed to search patients:', error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  // Search patients with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchPatients();
      }, 300);
    } else {
      setPatients([]);
      setShowDropdown(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, searchPatients]);

  const handleSelectPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    onSelectPatient(patient);
    setSearchTerm('');
    setShowDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleClearSelection = () => {
    setSelectedPatient(null);
    onSelectPatient(null as any);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || patients.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < patients.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : patients.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && patients[highlightedIndex]) {
          handleSelectPatient(patients[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getGenderIcon = (gender?: string) => {
    switch (gender) {
      case 'male':
        return '♂️';
      case 'female':
        return '♀️';
      default:
        return '⚪';
    }
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Selected Patient Display */}
      {selectedPatient ? (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <UserIcon className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-gray-900">
                  {patientName(selectedPatient)}
                </p>
                <div className="text-sm text-gray-600 space-x-3">
                  <span>{patientIdLabel(selectedPatient, operationMode)}</span>
                  {formatDobWithAge(selectedPatient.demographics.dateOfBirth) && (
                    <span>DOB: {formatDobWithAge(selectedPatient.demographics.dateOfBirth)}</span>
                  )}
                  {selectedPatient.demographics.gender && (
                    <span>{getGenderIcon(selectedPatient.demographics.gender)} {selectedPatient.demographics.gender}</span>
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClearSelection}
              className="text-sm text-blue-600 hover:text-blue-700"
              disabled={disabled}
            >
              Change
            </button>
          </div>
        </div>
      ) : null}

      {/* Search Input */}
      {!selectedPatient && (
        <>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => searchTerm.length >= 2 && setShowDropdown(true)}
              placeholder={searchPlaceholder}
              disabled={disabled}
              className={`
                w-full pl-10 pr-3 py-2 border rounded-md
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                disabled:opacity-50 disabled:cursor-not-allowed
                ${error ? 'border-red-300' : 'border-gray-300'}
              `}
            />
            {loading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              </div>
            )}
          </div>

          {/* Search Results Dropdown */}
          {showDropdown && patients.length > 0 && (
            <div
              ref={dropdownRef}
              className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
            >
              {patients.map((patient, index) => (
                <button
                  key={patient.id}
                  type="button"
                  className={`
                    w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
                    border-b border-gray-100 last:border-b-0
                    ${highlightedIndex === index ? 'bg-gray-50' : ''}
                  `}
                  onClick={() => handleSelectPatient(patient)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {patientName(patient)}
                      </p>
                      <div className="text-sm text-gray-600 space-x-3">
                        <span>{patientIdLabel(patient, operationMode)}</span>
                        {formatDobWithAge(patient.demographics.dateOfBirth) && (
                          <span>DOB: {formatDobWithAge(patient.demographics.dateOfBirth)}</span>
                        )}
                        {patient.demographics.gender && (
                          <span>{getGenderIcon(patient.demographics.gender)}</span>
                        )}
                      </div>
                    </div>
                    {selectedPatientId === patient.id && (
                      <CheckIcon className="w-5 h-5 text-primary-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {showDropdown && searchTerm.length >= 2 && patients.length === 0 && !loading && (
            <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg p-4">
              <p className="text-sm text-gray-500 text-center">No patients found</p>
            </div>
          )}
        </>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Help Text */}
      {!selectedPatient && !error && (
        <p className="mt-1 text-xs text-gray-500">
          Type at least 2 characters to search by name, email, phone, or ID
        </p>
      )}
    </div>
  );
};